'use client';

import { useCallback } from 'react';
import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { ApiClientError } from '@/shared/lib/api';
import { createLogger } from '@/shared/lib/logger';
import { useRenderStore } from '@/features/design/render/model/render-form.store';
import { renderSettingsSnapshot } from '@/features/design/render/lib/render-settings-patch';
import type {
  AssistantCardOutcome,
  AssistantCommandBlock,
  AssistantThread,
  AssistantTurnContext,
} from '@/types/assistant';
import { assistantService } from '../api/assistant.service';
import {
  scopeOf,
  TURN_MESSAGE_ID,
  undoKey,
  useAssistantStore,
} from '../model/assistant.store';
import type { AssistantScreenContext } from './use-assistant-screen';
import { useAssistantCommands } from './use-assistant-commands';

const logger = createLogger('Assistant');

/**
 * The conversation for a scope: the building's most recent thread, or a new
 * one. Inside a building that is the building's thread; outside, the general
 * one (ADR-001 §1).
 */
const threadQuery = (buildingId: string | null) => ({
  queryKey: cacheKeys.assistant.thread(scopeOf(buildingId)),
  queryFn: async (): Promise<AssistantThread> => {
    const [latest] = await assistantService.listThreads(buildingId);
    return latest ?? (await assistantService.createThread(buildingId));
  },
  staleTime: Infinity,
});

export function ensureThread(
  queryClient: QueryClient,
  buildingId: string | null
): Promise<AssistantThread> {
  return queryClient.fetchQuery(threadQuery(buildingId));
}

/** Store a card's outcome on the server; a lost request only costs the sync. */
function persistCardOutcome(
  threadId: string,
  messageId: string,
  blockIndex: number,
  outcome: AssistantCardOutcome
): void {
  assistantService
    .recordCardOutcome(threadId, messageId, blockIndex, outcome)
    .catch(() => logger.warn('Card outcome not stored', { messageId, blockIndex }));
}

export function useAssistantThread(
  screen: AssistantScreenContext,
  enabled: boolean
) {
  const queryClient = useQueryClient();
  const runCommand = useAssistantCommands();
  const { buildingId } = screen;
  const scope = scopeOf(buildingId);

  const thread = useQuery({ ...threadQuery(buildingId), enabled });
  const threadId = thread.data?.id ?? null;

  // The server holds the conversation; the panel refetches it when it opens
  // (mount), after every turn and when the app returns to the foreground.
  const messagesQuery = useQuery({
    queryKey: cacheKeys.assistant.messages(threadId ?? ''),
    queryFn: () => assistantService.listMessages(threadId!),
    enabled: enabled && !!threadId,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  // "New conversation" moves the open conversation to the history, resuming
  // brings one back, "Delete history" drops everything, the summary too. Each
  // time the panel re-reads the thread from the server (ADR-001, realtime
  // reconcile) and forgets the history list, which is re-read when it opens.
  const reloadThread = useCallback(async () => {
    if (!threadId) return;
    queryClient.removeQueries({ queryKey: cacheKeys.assistant.history(threadId) });
    await queryClient.refetchQueries({ queryKey: cacheKeys.assistant.messages(threadId) });
  }, [queryClient, threadId]);

  const startNewConversation = useCallback(async () => {
    if (!threadId) return;
    await assistantService.startSession(threadId);
    await reloadThread();
  }, [reloadThread, threadId]);

  const resumeSession = useCallback(
    async (session: number) => {
      if (!threadId) return;
      await assistantService.resumeSession(threadId, session);
      await reloadThread();
    },
    [reloadThread, threadId]
  );

  const deleteHistory = useCallback(async () => {
    if (!threadId) return;
    await assistantService.deleteHistory(threadId);
    await reloadThread();
  }, [reloadThread, threadId]);

  const send = useCallback(
    async (rawText: string) => {
      const text = rawText.trim();
      const store = useAssistantStore.getState();
      if (!text || !threadId || store.turn) return;

      const form = useRenderStore.getState();
      const formOnScreen =
        (screen.screen === 'design' || screen.screen === 'render') &&
        !!screen.designId &&
        form.currentContext?.conceptId === screen.designId;
      const skipped = store.takeSkippedFields();
      const context: AssistantTurnContext = {
        screen: screen.screen,
        building_id: buildingId,
        design_id: screen.designId,
        render_id: screen.renderId,
        render_settings: formOnScreen
          ? renderSettingsSnapshot(form.formData)
          : null,
        skipped_fields: skipped,
      };

      store.startTurn(scope, threadId, text);
      let blockIndex = 0;
      const patchTarget = { designId: screen.designId };
      try {
        const messageId = await assistantService.sendMessage(
          threadId,
          text,
          context,
          {
            onStatus: (state) => useAssistantStore.getState().setTurnStatus(state),
            onBlock: (block) => {
              const index = blockIndex++;
              useAssistantStore.getState().addTurnBlock(block);
              if (block.type === 'client_command') {
                runCommand(block as AssistantCommandBlock, {
                  target: patchTarget,
                  undoKey: undoKey(TURN_MESSAGE_ID, index),
                });
              }
            },
          }
        );
        // The turn may have opened a new conversation after days of silence.
        await reloadThread();
        // Cards pressed while the reply was still streaming had no message id
        // yet; they are stored now that the message has one.
        const turnPrefix = `${TURN_MESSAGE_ID}:`;
        const pressedMidTurn = Object.entries(
          useAssistantStore.getState().cardOutcomes
        ).filter(([key]) => key.startsWith(turnPrefix));
        useAssistantStore.getState().finishTurn(messageId);
        if (messageId) {
          for (const [key, outcome] of pressedMidTurn) {
            persistCardOutcome(threadId, messageId, Number(key.slice(turnPrefix.length)), outcome);
          }
        }
      } catch (error) {
        logger.warn('Assistant turn failed', {
          status: error instanceof ApiClientError ? error.status : undefined,
        });
        // Not reported to the server: report them again with the retry.
        useAssistantStore.getState().addSkippedFields(skipped);
        // A refusal the API worded is shown as is; a lost connection has no
        // server text and gets the panel's own.
        useAssistantStore
          .getState()
          .failTurn(
            error instanceof ApiClientError && error.status >= 400
              ? error.message
              : null
          );
      }
    },
    [buildingId, reloadThread, runCommand, scope, screen, threadId]
  );

  // A card pressed on a stored message: shown at once on this device, stored
  // on the card so every device and reload agree. A card of the reply still
  // streaming has no message id yet; `send` stores it when the reply ends.
  const recordCardOutcome = useCallback(
    (messageId: string, blockIndex: number, outcome: AssistantCardOutcome) => {
      useAssistantStore.getState().setCardOutcome(undoKey(messageId, blockIndex), outcome);
      if (threadId && messageId !== TURN_MESSAGE_ID) {
        persistCardOutcome(threadId, messageId, blockIndex, outcome);
      }
    },
    [threadId]
  );

  return {
    threadId,
    recordCardOutcome,
    messages: messagesQuery.data?.messages ?? [],
    startNewConversation,
    resumeSession,
    deleteHistory,
    isLoading: thread.isLoading || messagesQuery.isLoading,
    loadError: thread.error ?? messagesQuery.error ?? null,
    send,
  };
}
